import * as React from 'react';
import { useSelector, useDispatch } from "react-redux";
import '../styles.css';
//internal and external components import
import { ClickAwayListener, Table, Tooltip, Typography } from '@mui/material';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import StatusIndicatorSmall from '../../StatusIndicatorSmall';
import ProgressBar from '../../ProgressBar';
import { sortData } from '../../../common/SortSupport';
import { EnhancedTableHead } from '../../EnhancedTableHead';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo, faDownLeftAndUpRightToCenter, faPencil } from '@fortawesome/free-solid-svg-icons';
import { Box } from '@mui/material';
import { CustomTooltip } from '../../CustomTooltip';
import { colors } from '../../../common/pallette/colors';
import TableSkeleton from '../../TableSkeleton';
import { resetInterviewDetails, resetOnSiteStandards, resetSelectedOnSiteCriteria, resetSelectedOnSiteSession, setInterviewDetails, setIsFetchingInterviewDetails, setOnSiteCriteria, setSelectedOnSiteSession } from '../../../redux/reducers/evaluation';
import TableDateSelector from '../../TableDateSelector';
import TableTimePicker from '../../TableTimePicker';
import { formatDate, formatHHmmToDateTime, onsiteSelectedCriteriaRegex } from '../../../common/StaticExports';
import { getUsersByClub } from '../../../api/club';
import { setClubUsers, setSnackBarData, setUserRoles } from '../../../redux/reducers/common';
import { getClubReviewsInfo, getOnSiteCriteriaStandards, getOnSiteRoles, saveSessionDate, syncInterviewees, updateSessionDuration, updateSessionStartTime } from '../../../api/evaluation';
import { useTranslation } from 'react-i18next';
import OnSiteIntervieweeSelectBox from '../../OnSiteIntervieweeSelectBox';
import OnSiteRolesSelectBox from '../../OnSiteRolesSelectBox';
import MinutesPicker from '../../MinutesPicker';
import { AvatarAndName } from '../../AvatarAndName';
import { baseUrl } from '../../../api/config';
import { useLocation, useNavigate } from 'react-router';


export default function OnSiteCriteriaTable(props: any) {

  const { data, criteria, isLoading } = props;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [idToEdit, setIdToEdit] = React.useState(0);
  const onSiteCriteria = useSelector((state:any) => state.evaluation.onSiteCriteria);
  const selectedOnSiteSession = useSelector((state:any) => state.evaluation.selectedOnSiteSession);
  const interviewDetails = useSelector((state:any) => state.evaluation.interviewDetails);
  const isFetchingInterviewDetails = useSelector((state:any) => state.evaluation.isFetchingInterviewDetails);
  const currentClub = useSelector((state:any) => state.clubDetails);
  const [t] = useTranslation();
  const [isUpdate, setIsUpdate] = React.useState(false);
  const clubUsers = useSelector((state:any) => state.common.clubUsers);
  const rowRefs:any = React.useRef({});
  const [editedRowId, setEditedRowId] = React.useState(null);

  //supporting contents
  const [order, setOrder] = React.useState('');
  const [orderBy, setOrderBy] = React.useState('');

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: any,
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const visibleRows:any = React.useMemo(
    () =>
      data?.length > 0 &&
      sortData(data, orderBy, order),
    [order, orderBy, data],
  );

  const head = [
    {
      key: '', displayName: 'id',
    },
    {
      key: '', displayName: 'interview',
    },
    {
      key: '', displayName: 'date',
    },
    {
      key: '', displayName: 'start time',
    },
    {
      key: '', displayName: 'duration (mins)',
    },
    {
      key: '', displayName: 'interviewee',
    },
    {
      key: '', displayName: 'role',
    },
    {
      key: '', displayName: 'progress',
    },
    {
      key: '', displayName: '',
    },
  ]

  const manageTableExpand = () => {
    dispatch(resetSelectedOnSiteSession());
    dispatch(resetInterviewDetails());
    dispatch(resetOnSiteStandards());
    navigate('/onsite/sessions');
  }

  const manageTime = (timeProps:any) => {
    const { hours, minutes } = timeProps;
    return hours * 60 + minutes;
  }

  const manageEditOnClick = async (dataId:any) => {
    setIdToEdit(dataId);
    setEditedRowId(dataId);
    const response = await getUsersByClub({clubId: currentClub.id});
    dispatch(setClubUsers(response));
    const reviewData = await getClubReviewsInfo({clubId: currentClub?.id});
    const roles = await getOnSiteRoles({onSiteReviewId: reviewData?.onSiteReviewId});
    dispatch(setUserRoles(roles));
  }

  //update session start time api
  const manageStartTime = async (value:any, session:any, keyLabel:any) => {
    const [hours, minutes] = value.split(':');
    const response = await updateSessionStartTime({sessionId: session?.id, hours: hours, minutes: minutes});
    updateState(value, session, keyLabel);
  }

  //update session duration api
  const manageDuration = async (value:any, session:any, keyLabel:any) => {
    if (value !== null) {
      const response = await updateSessionDuration({sessionId: session?.id, duration: value});
      updateState(response, session, keyLabel);
      manageSnackBarData(idToEdit);
    }
  }

  //update session date api
  const manageDate = async (value:any, session:any, keyLabel:any) => {
    const date = formatDate(value);
    if (date != "1970-01-01") {
      const reviewData = await getClubReviewsInfo({clubId: currentClub?.id});
      const response = await saveSessionDate({onSiteReviewId: reviewData?.onSiteReviewId, sessionId: session?.id, sessionDate: date});
      updateState(response, session, keyLabel);
      manageSnackBarData(idToEdit);
    }
  }

  //update interviewee api
  const manageInterviewees = async (value:any, session:any, keyLabel:any) => {
    const reviewData = await getClubReviewsInfo({clubId: currentClub?.id});
    setIsUpdate(true);
    const response = await syncInterviewees({onSiteReviewId: reviewData?.onSiteReviewId, sessionId: session.id, interviewees: value});
    updateState(response, session, keyLabel);
  }

  const manageSnackBarData = (editedId:any) => {
    const ids = visibleRows?.map((item:any) => item.id);
    const conflicts = data?.filter((item:any) => item?.isInConflict == true);
    if(ids.includes(editedId) && conflicts.length > 0) {
      const warningData:any = {isOpen: true, severity: 'warning', message: t('INTERVIEW_CONFLICTS')};
      dispatch(setSnackBarData(warningData));
    }
  }
  const updateState = async (updatedValue:any, obj:any, keyLabel:any) => {
    let conflicts:any = [];
    let changedObj = obj;
    if (keyLabel == 'startTime' && false) {
      const [hours, minutes] = updatedValue.split(':').map(Number);
      changedObj = {...changedObj, [keyLabel]: {...changedObj[keyLabel], hours: hours, minutes: minutes} };
    } else {
      changedObj = {...changedObj, [keyLabel]: updatedValue};
    }
    let tempArr = onSiteCriteria;
    tempArr = tempArr.map((da:any, i:any) => {
      if (obj.id == da.id) {
        return changedObj;
      } return da;
    });
    dispatch(setOnSiteCriteria(tempArr));
    setIsUpdate(false);
  }

  const manageNullData = (row:any) => {
    const tempObj = onSiteCriteria?.find((d:any) => d.id == row);
    if (tempObj?.interviewees && tempObj.interviewees.length) {
      const tempArr = tempObj.interviewees.filter((item:any) => !(item.name == null && item.role == null && item.userId == null));
      updateState(tempArr, tempObj, 'interviewees');
    }
  }

  const fetchUserAvatars = (userId:any) => {
    const tempObj = clubUsers.find((d:any) => d.id == userId);
    if (tempObj) {
      return tempObj.avatarPath ? `${baseUrl}/Content${tempObj.avatarPath}` : ''
    } else {
      return '';
    }
  }

  const fetchSessionData = async (session:any) => {
    const trueBoolean:any = true;
    const falseBoolean:any = false;
    dispatch(setIsFetchingInterviewDetails(trueBoolean));
    await getClubReviewsInfo({clubId: currentClub?.id})
    .then((res:any) => { 
      getOnSiteCriteriaStandards({onSiteReviewId: res.onSiteReviewId, sessionId: session?.id})
      .then((result:any) => {
        dispatch(setInterviewDetails(result));
        dispatch(setIsFetchingInterviewDetails(falseBoolean));
      });
      });
  }

  React.useEffect(() => {
    if (!editedRowId) return;

    let attempts = 0;
    const maxAttempts = 20; // ~2 seconds if interval = 100ms

    const tryScroll = () => {
      const el = rowRefs.current[editedRowId];
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "nearest" });
        el.focus({ preventScroll: true });
      } else if (attempts < maxAttempts) {
        attempts++;
        setTimeout(tryScroll, 100); // retry until row is mounted
      }
    };

    tryScroll();
  }, [editedRowId, visibleRows]);

  React.useEffect(() => {
    if (!interviewDetails && selectedOnSiteSession && selectedOnSiteSession?.id && !isFetchingInterviewDetails) {
        fetchSessionData(selectedOnSiteSession);
    }
  }, [selectedOnSiteSession])


  return (
    <React.Fragment>
      <Box sx={{ position: 'relative' }}>
        {!criteria && <FontAwesomeIcon className='icon tableExpandIcon' icon={faDownLeftAndUpRightToCenter} onClick={() => manageTableExpand()} />}
          <TableContainer sx={{height: '100%', width: !criteria ? '100%' : 'auto', overflow: criteria ? 'auto' : 'hidden'}}>
            <ClickAwayListener onClickAway={() => {manageNullData(idToEdit); setIdToEdit(999);}}>
              <Table stickyHeader sx={{ width: isLoading ? '100%' : (criteria ? '100%' : 'auto'), boxShadow: 'none' }} size="small" aria-label="a dense table">
                <EnhancedTableHead
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleRequestSort}
                  tableHeads={criteria ? head : [head[0]]}
                />
                <TableBody>
                  {
                    (visibleRows?.length) ?
                    visibleRows.map((d: any, i: number) => (
                      <>
                      <TableRow
                        className={`${d.isInConflict ? 'conflictRow' : 'hoverBG' } ${idToEdit != d.id && 'cursorPointer'} ${editedRowId == d.id && 'selectedRow'} `}
                        key={d?.id}
                        ref={(el) => {
                          if (el) rowRefs.current[d.id] = el;
                        }}
                        onClick={() => {
                          if (idToEdit != d.id) {
                            dispatch(setSelectedOnSiteSession(d));
                            dispatch(resetSelectedOnSiteCriteria());
                            dispatch(resetOnSiteStandards());
                            dispatch(resetInterviewDetails(d));
                            fetchSessionData(d);
                            navigate(`/onsite/sessions/${d?.id}/criteria`);
                          }
                        }}
                      >
                        <CustomTooltip title={d.name} className={!criteria ? 'capitalize' : 'displayNone'} arrow followCursor placement='top-start'>
                          <TableCell className='capitalize' sx={{cursor: 'pointer'}} 
                          onClick={() => {
                            dispatch(setSelectedOnSiteSession(d));
                            navigate(`/onsite/sessions/${d?.id}/criteria`);
                          }}>
                          {i+1}
                          </TableCell>
                        </CustomTooltip>
                        {
                          criteria &&
                          <>
                          <TableCell className='capitalize' sx={{ width: '40%' }}>
                            {d.name}
                          </TableCell>
                          <TableCell sx={{ width: '120px' }}>
                            {
                              idToEdit != d.id ?
                              <Typography className='fs13'>{(d.date != undefined) ? formatDate(d.date) : '-'}</Typography> :
                              <TableDateSelector
                              variant={'standard'}
                              manageChange={(e:any) => {manageDate(new Date(e), d, 'date')}}
                              dateValue={d.date ? formatDate(d.date) : null} />
                            }
                          </TableCell>
                          <TableCell sx={{ width: '120px' }}>
                            {
                              idToEdit != d.id ?
                              <Typography className='fs13'>
                                {
                                  (d?.startTimeStr && d?.startTimeStr != '0:0') ?
                                  d?.startTime?.hours.toString().padStart(2, '0') + ':' + d?.startTime?.minutes.toString().padStart(2, '0') :
                                  '-'}</Typography> :
                              <TableTimePicker 
                              format={'HH:mm'}
                              views={['hours', 'minutes']}
                              manageChange={(e:any) => {manageStartTime(e, d, 'startTimeStr')}}
                              value={(d?.startTimeStr && d?.startTimeStr != '0:0') ? formatHHmmToDateTime(d.startTimeStr) : ''} />
                            }
                          </TableCell>
                          <TableCell sx={{ width: '120px' }}>
                            {
                              idToEdit != d.id ?
                              <Typography className='fs13'>{(d?.duration && d?.duration != 0) ? d?.duration : '-'}</Typography> :
                              <MinutesPicker
                              manageChange={(e:any) => {manageDuration(parseInt(e), d, 'duration')}}
                              value={(d?.duration && d?.duration != 0) ? d?.duration : null} />
                            }
                          </TableCell>
                          <TableCell sx={{ width: '180px' }} >
                            {
                              idToEdit != d.id ?
                              d.interviewees && d.interviewees.length ?
                              d.interviewees.map((interviewee:any) => 
                                <AvatarAndName 
                                    avatarSize={18}
                                    avatarName={interviewee?.name} 
                                    avatarImage={fetchUserAvatars(interviewee?.userId)}/>
                              ) :
                              <Typography className='fs13'>{'-'}</Typography>
                              :
                              d?.interviewees?.length && 
                              d?.interviewees.map((interviewee:any, j:any) => {
                                return(
                                  <OnSiteIntervieweeSelectBox
                                  value={interviewee}
                                  selectedOptions={d.interviewees || []}
                                  manageSelect={(e:any) => {
                                    let tempArr:any = [];
                                    if (e.role == null && e.name == null && e.userId == null) {
                                      tempArr= d.interviewees.filter((inter:any, index:any) => j!=index );
                                    } else {
                                      tempArr= d.interviewees.map((inter:any, index:any) => {return j==index ? e : inter});
                                    }
                                    updateState(tempArr, d, 'interviewees');
                                    manageInterviewees(tempArr, d, 'interviewees');
                                  }}
                                  />
                                )
                              })
                            }
                            {idToEdit == d.id && <Typography className='fs13' onClick={() => {
                              const newObj = {
                                id: null,
                                userId: null,
                                name: null,
                                departmentId: null,
                                role: null
                              };
                              const tempObj = [...d?.interviewees, newObj];
                              updateState(tempObj, d, 'interviewees');
                              }}>{'+ Add new interviewee'}</Typography>}
                          </TableCell>
                          <TableCell sx={{ width: '180px' }}>
                            {
                              idToEdit != d.id ?
                              d.interviewees && d.interviewees.length ?
                              d.interviewees.map((interviewee:any) => 
                                <Typography className='fs13'>{interviewee?.role ? interviewee?.role : '-'}</Typography>
                              ) :
                              <Typography className='fs13'>{'-'}</Typography>
                              :
                              d?.interviewees?.length && 
                              d?.interviewees.map((interviewee:any, j: any) => {
                                return(
                                  <OnSiteRolesSelectBox
                                  value={interviewee}
                                  manageSelect={(e:any) => {
                                    let tempArr:any = [];
                                    if (e.role == null && e.name == null && e.userId == null) {
                                      tempArr= d.interviewees.filter((inter:any, index:any) => j!=index );
                                    } else {
                                      tempArr= d.interviewees.map((inter:any, index:any) => {return j==index ? e : inter});
                                    }
                                    updateState(tempArr, d, 'interviewees');
                                    manageInterviewees(tempArr, d, 'interviewees');
                                  }}
                                  />
                                )
                              })
                            }
                            {idToEdit == d.id && <Typography className='fs13' sx={{ visibility: 'hidden' }}>{'+ Add new interviewee'}</Typography>}
                          </TableCell>
                          <TableCell>
                            {
                              d?.progress == 100 ?
                              <StatusIndicatorSmall reportType={'siteReview'} status={3}/> :
                              <ProgressBar percent={d.progress} width={'180px'}/>
                            }
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex' }}  onClick={(e) => {e.stopPropagation(); idToEdit != d.id && manageEditOnClick(d.id);}}>
                              <Box className={idToEdit == d.id ? 'cursorNotAllowed' : 'cursorPointer'}>
                                <FontAwesomeIcon className={'icon'} icon={faPencil} />
                              </Box>
                              {
                                d.isInConflict &&
                                <Box className={'infoIcon'}>
                                  <Tooltip title={'This interview schedule conflicts with an existing interview. Please adjust the date, time, or duration to avoid overlap.'} arrow placement='top'>
                                    <FontAwesomeIcon icon={faCircleInfo} color={colors.yellow.bright} fontSize='16px'/>
                                  </Tooltip>
                                </Box>
                              }
                            </Box>
                          </TableCell>
                          </>
                        }
                      </TableRow>
                      {/* DISABLED THE Row to show addtional roles and interviewer */}
                      {
                        false && criteria && d.interviewees && (d.interviewees.slice(1)).length > 0 && 
                        d.interviewees.slice(1).map((interviee:any, j:any) => (
                          <TableRow
                            className='hoverBG'
                            key={`additional-dynamic-table-row-${j}`}
                            // onClick={() => manageTableShrink(d)}
                          >
                            <TableCell>
                            </TableCell>
                            <TableCell>
                            </TableCell>
                            <TableCell sx={{ width: '120px' }}>
                            </TableCell>
                            <TableCell sx={{ width: '120px' }}>
                            </TableCell>
                            <TableCell sx={{ width: '120px' }}>
                            </TableCell>
                            <TableCell sx={{ width: '180px' }}>
                              <Typography className='fs13'>{interviee.name}</Typography>
                            </TableCell>
                            <TableCell sx={{ width: '180px' }}>
                              <Typography className='fs13'>{interviee.role}</Typography>
                            </TableCell>
                            <TableCell sx={{ width: '180px' }}>
                            </TableCell>
                            <TableCell>
                            </TableCell>
                          </TableRow>
                        ))
                      }
                      </>
                    ))
                    :
                    isLoading ?
                    <TableSkeleton rowCount={13} cellCount={head?.length} /> :
                    <TableRow>
                      <TableCell colSpan={head?.length} sx={{ textAlign: 'center'}}>
                        <Typography className='fs13'>{t('NO_RECORDS_FOUND')}</Typography>
                      </TableCell>
                    </TableRow>
                  }
                </TableBody>
              </Table>
            </ClickAwayListener>
          </TableContainer>
      </Box>
    </React.Fragment>
  );
}